<template>
  <a-select
    show-search
    :mode="multiple ? 'multiple' : 'default'"
    v-model="selectProxy"
    :allow-clear="allowClear"
    :filter-option="filterOption"
    :placeholder="loading ? 'Loading...' : placeholder"
    :disabled="disabled"
    :getPopupContainer="resolvePopupContainer"
    :max-tag-count="maxTagCount"
    :max-tag-placeholder="maxTagPlaceholder"
    @select="$emit('select', $event)"
    @change="$emit('change', $event)"
  >
    <a-select-option v-if="multiple" key="select-all" value="select-all">
      {{ allSelected ? 'Unselect All' : 'Select All' }}
    </a-select-option>

    <a-select-option v-for="option in options" :key="option[optionKey]" :value="option[optionKey]"
      ><slot name="option" :option="option">
        {{ option[name] }}
      </slot>
    </a-select-option>
    <template slot="dropdownRender" slot-scope="menu">
      <slot name="dropdown" />
      <a-spin tip="Loading..." :spinning="loading">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
  </a-select>
</template>

<script>
import { Select, Spin } from 'ant-design-vue'
import VNodes from '@/components/ui/v-nodes.vue'

export default {
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-spin': Spin,
    VNodes
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Array, Number],
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    name: {
      type: String,
      default: undefined
    },
    optionKey: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowClear: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    collapseAllSelected: {
      type: Boolean,
      default: true
    },
    popupContainer: {
      type: Function,
      default: undefined
    }
  },
  computed: {
    selectedValues() {
      return Array.isArray(this.value) ? this.value.filter(item => item !== 'select-all') : []
    },
    optionKeys() {
      return this.options.map(item => item[this.optionKey])
    },
    allSelected() {
      if (!this.multiple || !this.optionKeys.length || !this.selectedValues.length) return false

      const selectedValues = new Set(this.selectedValues)

      return this.optionKeys.every(item => selectedValues.has(item))
    },
    shouldCollapseAllSelected() {
      return this.multiple && this.collapseAllSelected && this.allSelected
    },
    maxTagCount() {
      return this.shouldCollapseAllSelected ? 0 : undefined
    },
    maxTagPlaceholder() {
      return this.shouldCollapseAllSelected ? 'All' : undefined
    },
    selectProxy: {
      get() {
        if (this.multiple) {
          return this.selectedValues
        }

        if (this.value && typeof this.value === 'string' && Number(this.value)) {
          return Number(this.value)
        }

        return this.value ?? undefined
      },
      set(newValue) {
        if (this.multiple) {
          if (newValue.includes('select-all')) {
            const selectedValues = newValue.filter(item => item !== 'select-all')

            if (this.optionKeys.every(item => selectedValues.includes(item))) {
              this.$emit('update:value', [])
            } else {
              this.$emit('update:value', [...this.optionKeys])
            }
          } else {
            this.$emit(
              'update:value',
              newValue.filter(val => val !== 'select-all')
            )
          }
        } else {
          this.$emit('update:value', newValue)
        }
      }
    }
  },
  methods: {
    filterOption(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
    },
    resolvePopupContainer(el) {
      if (this.popupContainer) return this.popupContainer(el)

      return el?.parentNode ?? document.body
    }
  }
}
</script>

<style scoped>
::v-deep .ant-select-selection {
  max-height: 200px;
  height: fit-content;
  overflow: auto;
}
</style>
